import React from 'react';
import ReactDOM from 'react-dom/client';
import { GamificationProvider } from '../hooks/GamificationContext';
import { AppSessionProvider, useAppSession } from '../hooks/AppSessionContext';
import { StepsProvider } from '../hooks/StepsContext';
import { PlayerLocationProvider } from '../game-map/PlayerLocationContext';
import { MapViewProvider } from '../map-view/MapViewContext';
import { LocationStoreProvider } from '../locations/LocationStoreContext';
import App from './App';
import PublicQuickMarkApp from './PublicQuickMarkApp';
import { getRuntime, isLoggedIn as runtimeIsLoggedIn, authStatusUrl } from '../utils/runtime';
import { initializeOneSignalForApp } from '../utils/onesignal';
import '../utils/inAppCamera';
import { GameAudioProvider } from '../game/audio/GameAudioContext';
import { EncounterProvider } from '../encounters/EncounterContext';

const rootElement = document.getElementById('pfandpirat-root');
const runtime = getRuntime();
const currentHash = String(window.location.hash || '').toLowerCase();
const isQuickMarkHash = currentHash === '#/quick-mark' || currentHash.startsWith('#/quick-mark?');
const runtimePublicQuickMark = runtime?.isPublicQuickMark === true || runtime?.isPublicQuickMark === '1';
const LOGIN_BRIDGE_PARAM = 'pfp_after_login';
const LOGIN_NEXT_PARAM = 'pfp_next';

function getAppBaseUrl() {
  return runtime?.appUrl || '/app/';
}

function getOverviewHash() {
  return '#/overview';
}

function normalizeNextHash(rawHash) {
  const value = String(rawHash || '').trim();
  if (!value) return getOverviewHash();
  return value.startsWith('#') ? value : `#${value}`;
}

function getLoginBridgeUrl(nextHash = getOverviewHash()) {
  const bridgeUrl = new URL(getAppBaseUrl(), window.location.origin);
  bridgeUrl.searchParams.set(LOGIN_BRIDGE_PARAM, '1');
  bridgeUrl.searchParams.set(LOGIN_NEXT_PARAM, normalizeNextHash(nextHash));
  return bridgeUrl.toString();
}

function buildLoginUrl(nextHash = getOverviewHash()) {
  const rawLoginUrl = runtime?.loginUrl || '/wp-login.php';
  const loginUrl = new URL(rawLoginUrl, window.location.origin);
  loginUrl.searchParams.set('redirect_to', getLoginBridgeUrl(nextHash));
  return loginUrl.toString();
}

async function getAuthStatus() {
  try {
    const response = await fetch(authStatusUrl, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    });

    const payload = await response.json().catch(() => null);
    const data = payload?.data ?? payload;

    if (!response.ok || !data || !data.logged_in) {
      return null;
    }

    return data;
  } catch (error) {
    return null;
  }
}

async function waitForAuthStatus({ attempts = 12, delay = 350 } = {}) {
  for (let index = 0; index < attempts; index += 1) {
    const authStatus = await getAuthStatus();
    if (authStatus?.logged_in) {
      return authStatus;
    }

    if (index < attempts - 1) {
      await new Promise((resolve) => window.setTimeout(resolve, delay));
    }
  }

  return null;
}

function applyAuthStatusToRuntime(authStatus) {
  if (!authStatus) return;

  if (!window.pfandpiratData) {
    window.pfandpiratData = {};
  }

  Object.assign(window.pfandpiratData, {
    isLoggedIn: true,
    user_id: Number(authStatus.user_id || 0),
    display_name: authStatus.display_name || '',
    user_name: authStatus.user_name || authStatus.display_name || '',
    nonce: authStatus.nonce || window.pfandpiratData.nonce || '',
    hybridLootMode: authStatus.hybridLootMode || window.pfandpiratData.hybridLootMode || 'off',
    isPublicQuickMark: false,
  });
}

function getCurrentUrl() {
  return new URL(window.location.href);
}

function getBridgeState() {
  const url = getCurrentUrl();
  const isAfterLogin = url.searchParams.get(LOGIN_BRIDGE_PARAM) === '1';
  const nextHash = normalizeNextHash(url.searchParams.get(LOGIN_NEXT_PARAM) || getOverviewHash());
  return { isAfterLogin, nextHash, url };
}

function moveToPostLoginTarget(nextHash = getOverviewHash()) {
  const cleanUrl = getCurrentUrl();
  cleanUrl.searchParams.delete(LOGIN_BRIDGE_PARAM);
  cleanUrl.searchParams.delete(LOGIN_NEXT_PARAM);
  cleanUrl.hash = normalizeNextHash(nextHash);
  window.history.replaceState({}, document.title, cleanUrl.toString());
}

function renderLoadingState(message = 'Anmeldung wird geprüft …') {
  if (!rootElement) return;
  rootElement.innerHTML = `
    <div style="min-height:60vh;display:flex;align-items:center;justify-content:center;padding:24px;">
      <div style="max-width:520px;width:100%;background:#fff;border:1px solid #e5e7eb;border-radius:24px;padding:28px;box-shadow:0 10px 30px rgba(0,0,0,0.08);text-align:center;">
        <div style="font-size:18px;font-weight:700;color:#0f3558;margin-bottom:8px;">Pfandpirat</div>
        <div style="font-size:16px;color:#334155;line-height:1.6;">${message}</div>
      </div>
    </div>
  `;
}

function renderLoginProblem(nextHash = getOverviewHash()) {
  if (!rootElement) return;
  const retryUrl = buildLoginUrl(nextHash);
  rootElement.innerHTML = `
    <div style="min-height:60vh;display:flex;align-items:center;justify-content:center;padding:24px;">
      <div style="max-width:560px;width:100%;background:#fff;border:1px solid #e5e7eb;border-radius:24px;padding:28px;box-shadow:0 10px 30px rgba(0,0,0,0.08);text-align:center;">
        <div style="font-size:18px;font-weight:700;color:#0f3558;margin-bottom:10px;">Anmeldung konnte noch nicht bestätigt werden</div>
        <div style="font-size:15px;color:#475569;line-height:1.7;margin-bottom:18px;">Die Seite wurde nach dem Login nicht sauber in die App zurückgeführt. Mit dem Button unten startest du den Login-Rücksprung einmal sauber neu, ohne in eine Endlosschleife zu laufen.</div>
        <a href="${retryUrl}" style="display:inline-flex;align-items:center;justify-content:center;padding:14px 20px;border-radius:16px;background:#2563eb;color:#fff;text-decoration:none;font-weight:700;">Erneut zur App anmelden</a>
      </div>
    </div>
  `;
}
function PrivateAppProviders() {
  const { session } = useAppSession();

  return (
    <GameAudioProvider>
      <GamificationProvider initialSnapshot={session?.gamification}>
        <PlayerLocationProvider userId={session?.user?.id} initialConsent={runtime?.locationConsent}>
          <EncounterProvider>
            <LocationStoreProvider>
              <StepsProvider initialSteps={session?.steps}>
                <MapViewProvider>
                  <App />
                </MapViewProvider>
              </StepsProvider>
            </LocationStoreProvider>
          </EncounterProvider>
        </PlayerLocationProvider>
      </GamificationProvider>
    </GameAudioProvider>
  );
}

function renderAppTree(isPublicQuickMark) {
  if (!rootElement) return;

  const appTree = isPublicQuickMark ? (
    <PublicQuickMarkApp />
  ) : (
    <AppSessionProvider initialSession={runtime?.bootstrapSession || null}>
      <PrivateAppProviders />
    </AppSessionProvider>
  );

  ReactDOM.createRoot(rootElement).render(appTree);
}


async function bootstrap() {
  let loggedIn = Boolean(runtimeIsLoggedIn);
  let isPublicQuickMark = runtimePublicQuickMark || (!loggedIn && isQuickMarkHash);
  const bridgeState = getBridgeState();

  if (bridgeState.isAfterLogin) {
    renderLoadingState('Anmeldung wird übernommen …');
  }

  if (!loggedIn) {
    const authStatus = bridgeState.isAfterLogin
      ? await waitForAuthStatus()
      : await getAuthStatus();

    if (authStatus?.logged_in) {
      applyAuthStatusToRuntime(authStatus);
      loggedIn = true;
      isPublicQuickMark = false;
      if (bridgeState.isAfterLogin) {
        moveToPostLoginTarget(bridgeState.nextHash);
      }
    }
  }

  if (!loggedIn && bridgeState.isAfterLogin) {
    renderLoginProblem(bridgeState.nextHash);
    return;
  }

  if (!loggedIn && !isPublicQuickMark) {
    window.location.replace(buildLoginUrl(getOverviewHash()));
    return;
  }

  if (loggedIn && !isPublicQuickMark) {
    initializeOneSignalForApp();
  }

  renderAppTree(isPublicQuickMark);
}

bootstrap();
